import React, {
 useMemo, useState, useEffect, useRef, Fragment,
} from 'react';
import TextArea from '../../../component/textAreaLimit/TextArea';
import DropDownList from '../../../component/iconDropDown/iconDropDown';
import { PrimaryButton, SecondaryButton } from '../../../component/buttonComponent/ButtonComponent';
import {
    VEHICLE_POLICY_FIELDS,
    FUEL_TYPE_FIELD_KEY,
    MICRO_REFILL_FIELD_KEY,
    OFFLINE_GAP_CAP_FIELD_KEY,
    DRAIN_WINDOW_FIELD_KEY,
    MAX_BURN_RATE_FIELD_KEY,
    isImmobilizerReqStatePolicyField,
    IMMOBILIZER_REQ_STATE_OPTIONS,
    STANDARD_DROPDOWN_CONFIGS,
    isEnableDisablePolicyField,
    isPolicyFieldVisible,
    ENABLE_OPTION,
    DISABLE_OPTION,
    BRAKE_DATA_SOURCE_FIELD_KEY,
    PROXY_FROM_FIELD_KEY,
    PROXY_TO_FIELD_KEY,
    IGN_RPM_MIN_FIELD_KEY,
    IGN_DC_VOLT_ON_FIELD_KEY,
    STOP_LENGTH_TO_CLOSE_FIELD_KEY,
    STOP_SPEED_FIELD_KEY,
    STOP_DRIFT_ALLOWANCE_FIELD_KEY,
} from './vehiclePolicyConfig';
import {
    mergeVehicleTypeOptions,
    findVehicleTypeOption,
    buildVehicleClassSelectOptions,
    normalizeVehicleClassKey,
    ADD_VEHICLE_TYPE_VALUE,
    MANAGE_VEHICLE_TYPES_VALUE,
    ADD_VEHICLE_CLASS_VALUE,
} from './vehicleTypeOptions';
import VehicleTypeManagerModal from './VehicleTypeManagerModal';
import {
    INITIAL_FIELD_BADGE,
    getFieldTooltip,
} from './vehiclePresetFieldUiMeta';
import { HardwarePolicyBindingsPanel } from './HardwarePolicyBindingsPanel';
import {
    mergeFuelTariffOptions,
    formatRegistryActivePrice,
} from './registryFuelTariffOptions';
import { isGnssFieldFilledByFuelPolicy } from './fuelPolicyGnssMapping';
import { getTripIdleFieldValuesForVehicleType, tripIdleFieldsDifferFromDefaults } from './vehicleBodyTypePresets';

export const GROUP = {
    FUEL_TANK: 'Fuel Tank Setup',
    ENERGY: 'Energy Efficiency (Opportunity Cost)',
    MOTION: 'Motion & Idling Logic',
    ACCEL: 'Accelerometer & Vibration Calibration',
    SECURITY: 'Security & Remote Immobilization',
    GNSS: 'GNSS Virtual Fuel (Mathematical Fallback)',
    BATTERY: 'Battery Diagnostic',
    ICE: 'ICE Health & Mechanical Diagnostics',
    EV: 'EV High-Voltage Health',
    TRIP: 'Trip Boundaries (Software Fallbacks)',
};

export const fieldsInGroup = (groupCategory) => (
    VEHICLE_POLICY_FIELDS
        .filter((f) => f.groupCategory === groupCategory)
        .map((f) => f.fieldKey)
);

const IMMOBILIZER_DOUT_OPTIONS = [
    { value: 1, label: 'DOUT 1' },
    { value: 2, label: 'DOUT 2' },
    { value: 3, label: 'DOUT 3' },
];

const DOUT_FIELD_KEY = 'Target Relay Output (Wired DOUT)';

const DISPLAY_LABELS = {
    [BRAKE_DATA_SOURCE_FIELD_KEY]: 'Braking',
    [MICRO_REFILL_FIELD_KEY]: 'Micro-refill',
    [OFFLINE_GAP_CAP_FIELD_KEY]: 'Offline gap cap',
    [DRAIN_WINDOW_FIELD_KEY]: 'Drain window',
    [MAX_BURN_RATE_FIELD_KEY]: 'Max burn rate',
    'Software Trip Start Speed (km/h)': 'Trip Start',
    'Ignition OFF Trip End Timeout (Sec)': 'Ign OFF Timeout',
    'Minimum Distance to Force Trip Start (km)': 'Force Start Distance',
    'Minimum Total Distance for Valid Trip (km)': 'Min Trip Distance',
    [IGN_RPM_MIN_FIELD_KEY]: 'RPM Running Threshold',
    [IGN_DC_VOLT_ON_FIELD_KEY]: 'Voltage Running Threshold',
    [STOP_LENGTH_TO_CLOSE_FIELD_KEY]: 'Stop Length to Close',
    [STOP_SPEED_FIELD_KEY]: 'Stop Speed',
    [STOP_DRIFT_ALLOWANCE_FIELD_KEY]: 'Stop Drift Allowance',
    'Optimal Max Refill Limit (%)': 'Optimal Refill',
    'Optimal Max EV Charge Limit (%)': 'Optimal Charge',
    'Receipt Calibration Multiplier': 'Receipt Calib.',
    'Maximum Tank Capacity': 'Tank capacity',
    'Minimum Refill Detection': 'Refill threshold',
    'Theft/Drain Detection Alert': 'Drain (theft)',
    'Sensor Noise/Spike Filter (L)': 'Spike filter',
    'Fuel Stabilization Wait Time (Sec)': 'Slosh Duration',
    'Allowed Stabilization Fluctuation (L)': 'Slosh tolerance',
    'High Impact / Crash Sensitivity (G)': 'Crash Trigger',
    'Excessive Idling/Standby Timeout (Sec)': 'Excessive Idling/Standby Timeout',
    'Minimum Speed to Register Movement (km/h)': 'Minimum Speed to Register Movement',
    'Max Excusable Idle Duration (Sec)': 'Max Excusable Idle Duration',
    'Hot Threshold (°C)': 'Hot Threshold',
    'Cold Threshold (°C)': 'Cold Threshold',
    [PROXY_FROM_FIELD_KEY]: 'Proxy From',
    [PROXY_TO_FIELD_KEY]: 'Proxy To',
    'Stop-and-go Merge (Sec)': 'Stop-and-go Merge',
    'Creep Limit (km/h)': 'Creep Limit',
    'Weak Battery Cranking Alert (Volts)': 'Weak Battery Cranking Alert',
    'Cranking Battery Failure Limit (Volts)': 'Cranking Battery Failure Limit',
    'Alternator Charging Minimum (Volts)': 'Alternator Charging Minimum',
    'Alternator Failure Wait Time (Sec)': 'Alternator Failure Wait Time',
    'Max Parked Parasitic Drain (V/hr)': 'Max Parked Parasitic Drain',
    'Safe State Hold Duration (Sec)': 'Safe State Hold Duration',
    'Max Safe HV Battery Temperature (°C)': 'Max Safe HV Battery Temperature',
    'Battery Thermal Runaway Delay (Min)': 'Battery Thermal Runaway Delay',
    'Engine Redline Limit (RPM)': 'Engine Redline Limit',
    'Over-Revving Warning Delay (Sec)': 'Over-Revving Warning Delay',
    'Aggressive Throttle Limit (%)': 'Aggressive Throttle Limit',
    'Aggressive Throttle Delay (Sec)': 'Aggressive Throttle Delay',
    'High Engine Load / Strain Limit (%)': 'High Engine Load / Strain Limit',
    'High Engine Load Warning Delay (Sec)': 'High Engine Load Warning Delay',
    'Max Safe Coolant Temperature (°C)': 'Max Safe Coolant Temperature',
    'Overheating Critical Alarm Delay (Sec)': 'Overheating Critical Alarm Delay',
    'Cold Engine Threshold (°C)': 'Cold Engine Threshold',
    'Cold Engine Abuse Delay (Min)': 'Cold Engine Abuse Delay',
    'Idling Burn Rate (L/hr)': 'Idling Burn Rate',
    'City Consumption Rate (L/100km)': 'City Consumption Rate',
    'Max City Speed Zone (km/h)': 'Max City Speed Zone',
    'Average Consumption Rate (L/100km)': 'Average Consumption Rate',
    'Max Average Speed Zone (km/h)': 'Max Average Speed Zone',
    'Highway Consumption Rate (L/100km)': 'Highway Consumption Rate',
    'Highway Speed Zone Start (km/h)': 'Highway Speed Zone Start',
    'Overspeeding Wind Resistance Penalty (%)': 'Overspeeding Wind Resistance Penalty',
    'Overspeeding Penalty Interval (km/h)': 'Overspeeding Penalty Interval',
};

const FUEL_VOLUME_L_FIELDS = new Set([
    'Maximum Tank Capacity',
    'Minimum Refill Detection',
    'Theft/Drain Detection Alert',
]);

const extractUnit = (fieldKey) => {
    if (FUEL_VOLUME_L_FIELDS.has(fieldKey)) {
        return 'L';
    }
    const match = /\(([^)]+)\)\s*$/.exec(fieldKey);
    return match ? match[1] : '';
};

const PanelTitleIcon = ({ name }) => {
    const common = { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' };
    switch (name) {
        case 'identity':
            return (
                <svg {...common} aria-hidden>
                    <circle cx="12" cy="8" r="4" strokeWidth="2" />
                    <path strokeWidth="2" d="M5 21a7 7 0 0114 0" />
                </svg>
            );
        case 'dataSource':
            return (
                <svg {...common} aria-hidden>
                    <rect x="3" y="4" width="18" height="16" rx="2" strokeWidth="2" />
                    <path strokeWidth="2" d="M8 8h8M8 12h8M8 16h4" />
                </svg>
            );
        case 'motion':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            );
        case 'fuelPolicy':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2.7l5.7 5.7a8 8 0 11-11.3 0z" /></svg>
            );
        case 'fuelTariff':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2" /></svg>
            );
        case 'battery':
            return (
                <svg {...common} aria-hidden><rect x="2" y="7" width="18" height="10" rx="2" strokeWidth="2" /></svg>
            );
        case 'immobilizer':
            return (
                <svg {...common} aria-hidden>
                    <rect x="3" y="11" width="18" height="11" rx="2" strokeWidth="2" />
                    <path strokeWidth="2" d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
            );
        case 'accelerometer':
            return (
                <svg {...common} aria-hidden>
                    <circle cx="12" cy="12" r="2" strokeWidth="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v2M12 18v2M4 12h2M18 12h2" />
                </svg>
            );
        case 'energy':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14" /></svg>
            );
        case 'trip':
            return (
                <svg {...common} aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.7 16.7L13.4 21a2 2 0 01-2.8 0L6.3 16.7a8 8 0 1111.3 0z" />
                    <circle cx="12" cy="11" r="3" strokeWidth="2" />
                </svg>
            );
        case 'hv':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            );
        case 'ice':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.3 6.3a4.5 4.5 0 000 6.4L12 20.4l7.7-7.7a4.5 4.5 0 00-6.4-6.4L12 7.6l-1.3-1.3a4.5 4.5 0 00-6.4 0z" /></svg>
            );
        case 'gnss':
            return (
                <svg {...common} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v18M3 12h18M5 5l14 14M19 5L5 19" /></svg>
            );
        default:
            return null;
    }
};

const SegToggle = ({ value, onChange, disabled }) => {
    const on = value === ENABLE_OPTION;
    const offActive = value === DISABLE_OPTION || value === '';
    return (
        <div className="vc-seg fr-seg" role="group" aria-label="Toggle">
            <button
              type="button"
              className={`vc-seg-btn${offActive ? ' vc-seg-btn-active' : ''}`}
              disabled={disabled}
              onClick={() => onChange(DISABLE_OPTION)}
            >
                Off
            </button>
            <button
              type="button"
              className={`vc-seg-btn${on ? ' vc-seg-btn-active' : ''}`}
              disabled={disabled}
              onClick={() => onChange(ENABLE_OPTION)}
            >
                On
            </button>
        </div>
    );
};

const badgeSpan = (status) => {
    if (!status) return null;
    if (status === 'lock') return <span className="bdg bdg-lock">Lock</span>;
    return null;
};

const inputStateClass = (badge, locked) => {
    if (locked) return 'vc-fr-in-ro ro fr-in';
    return 'fr-in';
};

const ddStateClass = (badge, locked) => {
    if (locked) return 'vc-dd-state-lock';
    return '';
};

function usePresetFormTooltip(rootRef) {
    const tipLayerRef = useRef(null);
    const [tip, setTip] = useState({
 visible: false, x: 0, y: 0, title: '', desc: '',
});
    const activeRef = useRef(null);

    useEffect(() => {
        const clamp = (x, y) => {
            const pad = 12;
            const el = tipLayerRef.current;
            const w = el?.offsetWidth ?? 280;
            const h = el?.offsetHeight ?? 120;
            let nx = x + 14;
            let ny = y + 14;
            if (nx + w > window.innerWidth - pad) nx = window.innerWidth - w - pad;
            if (ny + h > window.innerHeight - pad) ny = window.innerHeight - h - pad;
            if (nx < pad) nx = pad;
            if (ny < pad) ny = pad;
            return { nx, ny };
        };

        const showFor = (el, clientX, clientY) => {
            const title = el.getAttribute('data-tt-title');
            if (!title) return;
            activeRef.current = el;
            const desc = el.getAttribute('data-tt-desc') || '';
            const { nx, ny } = clamp(clientX, clientY);
            setTip({
 visible: true, x: nx, y: ny, title, desc,
});
        };

        const onMove = (e) => {
            if (!activeRef.current) return;
            const { nx, ny } = clamp(e.clientX, e.clientY);
            setTip((prev) => (prev.visible ? { ...prev, x: nx, y: ny } : prev));
        };

        const onOver = (e) => {
            const root = rootRef.current;
            if (!root) return;
            const el = e.target?.closest?.('.has-tooltip');
            if (!el || !root.contains(el)) return;
            showFor(el, e.clientX, e.clientY);
        };

        const onOut = (e) => {
            const root = rootRef.current;
            if (!root || !activeRef.current) return;
            const rel = e.relatedTarget;
            if (rel && (activeRef.current.contains(rel) || activeRef.current === rel)) return;
            if (rel && root.contains(rel) && rel.closest?.('.has-tooltip') === activeRef.current) return;
            activeRef.current = null;
            setTip((t) => ({ ...t, visible: false }));
        };

        document.addEventListener('mouseover', onOver, true);
        document.addEventListener('mouseout', onOut, true);
        document.addEventListener('mousemove', onMove, true);
        return () => {
            document.removeEventListener('mouseover', onOver, true);
            document.removeEventListener('mouseout', onOut, true);
            document.removeEventListener('mousemove', onMove, true);
        };
    }, [rootRef]);

    const layer = tip.visible ? (
        <div
          ref={tipLayerRef}
          className="vc-global-tooltip global-tooltip"
          style={{ left: tip.x, top: tip.y }}
          role="tooltip"
        >
            <div className="vc-global-tooltip-hdr global-tooltip-hdr">
                <span className="vc-global-tooltip-title global-tooltip-title">{tip.title}</span>
            </div>
            <div className="vc-global-tooltip-body global-tooltip-body">{tip.desc}</div>
        </div>
    ) : null;

    return layer;
}

const EMPTY_LOCKED_FIELD_KEYS = Object.freeze([]);

/**
 * Shared vehicle preset form: panel grid + horizontal label/control rows (`addVehicleConfig.css`).
 */
export const VehiclePresetFormGrid = ({
    // The preset name identifies the global preset, so it is only editable where a preset is
    // authored (the admin console). It is hidden in the per-device override modal.
    showPresetName = true,
    presetName,
    setPresetName,
    vehicleType,
    setVehicleType,
    descriptionValue,
    setDescriptionValue,
    fieldValues,
    onFieldChange,
    /** Optional atomic multi-field update (preferred when applying body-type defaults). */
    onFieldValuesPatch,
    vehicleTypeInputStyle,
    fuelPolicyOptions = [],
    selectedFuelPolicy,
    onFuelPolicyChange,
    batteryPolicyOptions = [],
    selectedBatteryPolicy,
    onBatteryPolicyChange,
    fuelTariffOptions = [],
    registryPriceByValue = {},
    registryOptionsLoading = false,
    // Vehicle types come from `GET /v3/vehicleTypes`. Adding and deactivating them are System
    // Administrator only, so both action rows in the dropdown are gated by `canManageVehicleTypes` —
    // same admin-vs-device-override split as `showPresetName` above (this form is also reached by
    // non-admins via the device preset modal).
    vehicleTypeOptions = [],
    vehicleTypesLoading = false,
    vehicleClassByName = {},
    canManageVehicleTypes = false,
    onAddVehicleType,
    /* Field keys this caller may not write, rendered read-only with the LOCK badge. The
     * device preset modal passes the Super Admin battery fields; the admin console passes
     * nothing. Same admin-vs-device split as `showPresetName` above. */
    lockedFieldKeys = EMPTY_LOCKED_FIELD_KEYS,
}) => {
    const rootRef = useRef(null);
    const tooltipLayer = usePresetFormTooltip(rootRef);

    // Adding a type follows the EvTariffModal precedent: a sentinel option in the dropdown reveals a
    // text input BELOW the select. Deliberately not an input inside the menu — react-select refocuses
    // its own search input whenever focus lands inside the menu list, which makes an in-menu field
    // unusable (keystrokes go to the hidden select input and filter the options instead).
    const [addTypeDraft, setAddTypeDraft] = useState('');
    const [addClassDraft, setAddClassDraft] = useState('');
    const [selectedAddClass, setSelectedAddClass] = useState(null);
    const [isAddingType, setIsAddingType] = useState(false);
    const [isAddingClass, setIsAddingClass] = useState(false);
    const [addTypePending, setAddTypePending] = useState(false);
    const [addTypeError, setAddTypeError] = useState('');
    const [isManagingTypes, setIsManagingTypes] = useState(false);

    // A saved preset may reference a type that has since been deactivated, or one that predates the
    // vehicleTypes collection. Inject it so the field shows the stored value instead of going blank.
    const mergedVehicleTypeOptions = useMemo(
        () => mergeVehicleTypeOptions(vehicleTypeOptions, vehicleType),
        [vehicleTypeOptions, vehicleType],
    );

    // Admin-only action rows live in their own group so react-select keeps body-type optgroups
    // intact (mixing flat options with groups flattens the menu in some builds).
    const vehicleTypeSelectOptions = useMemo(() => (
        canManageVehicleTypes
            ? [
                ...mergedVehicleTypeOptions,
                {
                    label: 'ACTIONS',
                    options: [
                        { value: ADD_VEHICLE_TYPE_VALUE, label: '+ Add new vehicle type' },
                        { value: MANAGE_VEHICLE_TYPES_VALUE, label: 'Manage vehicle types' },
                    ],
                },
            ]
            : mergedVehicleTypeOptions
    ), [mergedVehicleTypeOptions, canManageVehicleTypes]);

    const vehicleClassSelectOptions = useMemo(
        () => buildVehicleClassSelectOptions(mergedVehicleTypeOptions, { includeAdd: true }),
        [mergedVehicleTypeOptions],
    );

    const selectedVehicleTypeOption = useMemo(
        () => findVehicleTypeOption(mergedVehicleTypeOptions, vehicleType),
        [mergedVehicleTypeOptions, vehicleType],
    );

    // Bumped on every close/open so a request that resolves after the user cancelled cannot apply its
    // result — otherwise a slow add would silently set the preset's vehicleType post-cancel, and a
    // slow failure would write an error into state that is no longer rendered.
    const addTypeRunRef = useRef(0);
    // Stash the dropdown selection while the inline add form is open so Cancel can restore it.
    const vehicleTypeBeforeAddRef = useRef('');

    const closeAddType = ({ restorePreviousType = false } = {}) => {
        addTypeRunRef.current += 1;
        if (restorePreviousType && vehicleTypeBeforeAddRef.current) {
            setVehicleType(vehicleTypeBeforeAddRef.current);
        }
        vehicleTypeBeforeAddRef.current = '';
        setIsAddingType(false);
        setIsAddingClass(false);
        setAddTypeDraft('');
        setAddClassDraft('');
        setSelectedAddClass(null);
        setAddTypePending(false);
        setAddTypeError('');
    };

    const applyTripIdleDefaultsForVehicleType = (typeName, extraHints = {}) => {
        const patch = getTripIdleFieldValuesForVehicleType(typeName, {
            vehicleClass: extraHints.vehicleClass || vehicleClassByName[typeName],
        });
        if (!patch || Object.keys(patch).length === 0) {
            return;
        }
        if (typeof onFieldValuesPatch === 'function') {
            onFieldValuesPatch(patch);
            return;
        }
        Object.entries(patch).forEach(([fieldKey, value]) => {
            onFieldChange(fieldKey, value);
        });
    };

    const handleVehicleTypeSelect = (option) => {
        // Both sentinels open admin UI without committing a vehicleType; ADD additionally parks the
        // current selection in vehicleTypeBeforeAddRef so Cancel can restore it. Gated on the role as
        // well as on rendering, so a non-admin who somehow receives a sentinel value gets no admin UI.
        if (!canManageVehicleTypes
            && (option?.value === ADD_VEHICLE_TYPE_VALUE || option?.value === MANAGE_VEHICLE_TYPES_VALUE)) {
            return;
        }
        if (option?.value === ADD_VEHICLE_TYPE_VALUE) {
            vehicleTypeBeforeAddRef.current = String(vehicleType || '').trim();
            setVehicleType('');
            setAddTypeError('');
            setAddTypeDraft('');
            setAddClassDraft('');
            setSelectedAddClass(null);
            setIsAddingClass(false);
            setIsAddingType(true);
            return;
        }
        if (option?.value === MANAGE_VEHICLE_TYPES_VALUE) {
            setIsManagingTypes(true);
            return;
        }
        const nextType = option?.value || '';
        // While the inline add form is open vehicleType is '' (parked in vehicleTypeBeforeAddRef), so
        // compare against the stash — otherwise picking an existing type from the still-live dropdown
        // would skip the destructive-change confirm and replace tuned values silently.
        const previousType = String(vehicleTypeBeforeAddRef.current || vehicleType || '').trim();
        if (
            nextType
            && previousType
            && previousType !== nextType
            && tripIdleFieldsDifferFromDefaults(fieldValues, previousType, {
                vehicleClass: vehicleClassByName[previousType],
            })
        ) {
            const confirmed = window.confirm(
                'Changing vehicle type replaces Motion & Idling and Trip Boundary values with defaults for the new type. Continue?',
            );
            if (!confirmed) return;
        }
        // Picking a real type supersedes an open add form: close it without restoring, so Cancel can
        // no longer revert vehicleType to a stale selection while the boundary fields hold the new
        // type's defaults.
        if (isAddingType || isAddingClass) {
            closeAddType();
        }
        setVehicleType(nextType);
        if (nextType) {
            applyTripIdleDefaultsForVehicleType(nextType, { vehicleClass: option?.vehicleClass });
        }
    };

    const handleAddClassSelect = (option) => {
        if (option?.value === ADD_VEHICLE_CLASS_VALUE) {
            setIsAddingClass(true);
            setSelectedAddClass(null);
            setAddClassDraft('');
            return;
        }
        setIsAddingClass(false);
        setAddClassDraft('');
        setSelectedAddClass(option || null);
    };

    const commitAddType = async () => {
        const trimmed = addTypeDraft.trim();
        if (!trimmed) {
            setAddTypeError('Enter a body type name');
            return;
        }
        const classKey = isAddingClass
            ? normalizeVehicleClassKey(addClassDraft)
            : (selectedAddClass?.value || '');
        if (!classKey) {
            setAddTypeError(isAddingClass ? 'Enter a vehicle class' : 'Select a vehicle class');
            return;
        }
        if (typeof onAddVehicleType !== 'function') {
            closeAddType({ restorePreviousType: true });
            return;
        }
        const run = addTypeRunRef.current;
        setAddTypePending(true);
        setAddTypeError('');
        try {
            const created = await onAddVehicleType(trimmed, { vehicleClass: classKey });
            if (addTypeRunRef.current !== run) return;
            const createdName = typeof created === 'string' ? created : trimmed;
            setVehicleType(createdName);
            applyTripIdleDefaultsForVehicleType(createdName, { vehicleClass: classKey });
            closeAddType();
        } catch (error) {
            if (addTypeRunRef.current !== run) return;
            setAddTypePending(false);
            setAddTypeError(error?.message || 'Could not add the vehicle type');
        }
    };

    const fuelPolicyKeys = useMemo(() => (
        fieldsInGroup(GROUP.FUEL_TANK).filter((k) => k !== FUEL_TYPE_FIELD_KEY)
    ), []);

    const gnssPanelKeys = useMemo(() => (
        fieldsInGroup(GROUP.GNSS).filter((k) => k !== 'Receipt Calibration Multiplier')
    ), []);

    const accelAxisKeys = useMemo(() => (
        fieldsInGroup(GROUP.ACCEL).filter((k) => k.includes('-Axis'))
    ), []);

    const fuelPolicyLinked = Boolean(selectedFuelPolicy?.value);

    const fuelTariffDropdownOptions = useMemo(
        () => mergeFuelTariffOptions(fuelTariffOptions, fieldValues[FUEL_TYPE_FIELD_KEY]),
        [fuelTariffOptions, fieldValues[FUEL_TYPE_FIELD_KEY]],
    );

    const activePriceDisplay = useMemo(
        () => formatRegistryActivePrice(
            fieldValues[FUEL_TYPE_FIELD_KEY],
            registryPriceByValue,
            { loading: registryOptionsLoading },
        ),
        [fieldValues, registryPriceByValue, registryOptionsLoading],
    );

    const renderTooltipLabel = (displayText, tooltipId, badge) => {
        const { title, desc } = getFieldTooltip(tooltipId);
        return (
            <div
              className="vc-fr-lbl fr-lbl has-tooltip"
              data-tt-title={title}
              data-tt-desc={desc}
            >
                {displayText}
                {badge ? (
<>
{' '}
{badgeSpan(badge)}
</>
) : null}
            </div>
        );
    };

    const renderPolicyFieldInput = (fieldKey, { disabled, badge } = {}) => {
        const locked = Boolean(disabled) || badge === 'lock';
        const inhCust = inputStateClass(badge, locked);
        const ddWrap = ddStateClass(badge, locked);

        if (isEnableDisablePolicyField(fieldKey)) {
            return (
                <SegToggle
                  value={fieldValues[fieldKey] ?? ''}
                  disabled={locked}
                  onChange={(v) => onFieldChange(fieldKey, v)}
                />
            );
        }

        if (fieldKey === DOUT_FIELD_KEY) {
            const raw = fieldValues[fieldKey];
            const num = raw === '' || raw == null ? null : Number(raw);
            const selected = IMMOBILIZER_DOUT_OPTIONS.find((o) => o.value === num) || null;
            return (
                <div className={`vc-dd-wrap ${ddWrap}`.trim()}>
                    <DropDownList
                      placeholder="Select DOUT"
                      selectedValue={selected}
                      setSelectedValue={(option) => onFieldChange(fieldKey, option?.value ?? '')}
                      options={IMMOBILIZER_DOUT_OPTIONS}
                      width="100%"
                      maxWidth="100%"
                    />
                </div>
            );
        }

        if (fieldKey === FUEL_TYPE_FIELD_KEY) {
            const selected = fuelTariffDropdownOptions.find(
                (option) => option.value === fieldValues[fieldKey],
            ) || null;
            return (
                <div className={`vc-dd-wrap ${ddWrap}`.trim()}>
                    <DropDownList
                      placeholder={registryOptionsLoading ? 'Loading registry…' : 'Select'}
                      selectedValue={selected}
                      setSelectedValue={(option) => onFieldChange(fieldKey, option?.value || '')}
                      options={fuelTariffDropdownOptions}
                      width="100%"
                      maxWidth="100%"
                    />
                </div>
            );
        }

        const dropdownConfig = STANDARD_DROPDOWN_CONFIGS.find(({ predicate }) => predicate(fieldKey));
        if (dropdownConfig) {
            const { placeholder, options } = dropdownConfig;
            return (
                <div className={`vc-dd-wrap ${ddWrap}`.trim()}>
                    <DropDownList
                      placeholder={placeholder}
                      selectedValue={options.find((option) => option.value === fieldValues[fieldKey]) || null}
                      setSelectedValue={(option) => onFieldChange(fieldKey, option?.value || '')}
                      options={options}
                      width="100%"
                      maxWidth="100%"
                    />
                </div>
            );
        }

        if (isImmobilizerReqStatePolicyField(fieldKey)) {
            return (
                <div className={`vc-dd-wrap ${ddWrap}`.trim()}>
                    <DropDownList
                      placeholder="Select motion status"
                      selectedValue={IMMOBILIZER_REQ_STATE_OPTIONS.find((option) => String(option.value) === String(fieldValues[fieldKey])) || null}
                      setSelectedValue={(option) => onFieldChange(fieldKey, option?.value != null && option?.value !== '' ? String(option.value) : '')}
                      options={IMMOBILIZER_REQ_STATE_OPTIONS}
                      width="100%"
                      maxWidth="100%"
                    />
                </div>
            );
        }

        return (
            <input
              name={fieldKey}
              autoComplete="off"
              type="number"
              className={`vc-fr-in fr-in ${inhCust}`.replace(/\s+/g, ' ').trim()}
              disabled={locked}
              placeholder={fieldKey === MICRO_REFILL_FIELD_KEY ? 'Off' : undefined}
              value={fieldValues[fieldKey] ?? ''}
              onChange={(e) => onFieldChange(fieldKey, e.target.value)}
            />
        );
    };

    const renderFieldRow = (fieldKey, { wide, disabled } = {}) => {
        if (!isPolicyFieldVisible(fieldKey, fieldValues)) {
            return null;
        }
        const label = DISPLAY_LABELS[fieldKey] || fieldKey;
        const badge = INITIAL_FIELD_BADGE[fieldKey] ?? null;
        const lockedByFuelPolicy = fuelPolicyLinked && isGnssFieldFilledByFuelPolicy(fieldKey);
        const lockedByPermission = lockedFieldKeys.includes(fieldKey);
        // A permission lock shows the LOCK badge; a fuel-policy lock keeps its own styling.
        let resolvedBadge = badge;
        if (lockedByPermission) {
            resolvedBadge = 'lock';
        } else if (lockedByFuelPolicy) {
            resolvedBadge = null;
        }
        const control = renderPolicyFieldInput(fieldKey, {
            disabled: Boolean(disabled) || lockedByFuelPolicy || lockedByPermission,
            badge: resolvedBadge,
        });
        const unit = extractUnit(fieldKey);
        const useUnitWrap = unit
            && !isEnableDisablePolicyField(fieldKey)
            && fieldKey !== DOUT_FIELD_KEY
            && !STANDARD_DROPDOWN_CONFIGS.some(({ predicate }) => predicate(fieldKey))
            && !isImmobilizerReqStatePolicyField(fieldKey);

        const controlCell = useUnitWrap ? (
            <div className="vc-with-unit with-unit" data-u={unit}>
                {control}
            </div>
        ) : (
            <div className="vc-fr-control">{control}</div>
        );

        return (
            <Fragment key={fieldKey}>
                <div className={`vc-fr fr${wide ? ' vc-fr-wide fr-wide' : ''}`}>
                    {renderTooltipLabel(label, fieldKey, resolvedBadge)}
                    {controlCell}
                </div>
            </Fragment>
        );
    };

    return (
        <div ref={rootRef} className="vc-preset-form-root">
            <div className="vc-grid">
                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="identity" />
                                Asset Identity
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {showPresetName && (
                                <div className="vc-fr fr">
                                    {renderTooltipLabel('Vehicle Preset Name', '__presetName__', null)}
                                    <input
                                      name="presetName"
                                      autoComplete="off"
                                      type="text"
                                      className="vc-fr-in fr-in"
                                      style={vehicleTypeInputStyle}
                                      value={presetName}
                                      onChange={(e) => setPresetName(e.target.value)}
                                    />
                                </div>
                            )}
                            <div className="vc-fr fr">
                                {renderTooltipLabel('Vehicle type', '__vehicleType__', null)}
                                <div className="vc-dd-wrap">
                                    <DropDownList
                                      placeholder={vehicleTypesLoading ? 'Loading vehicle types…' : 'Select vehicle type'}
                                      selectedValue={selectedVehicleTypeOption}
                                      setSelectedValue={handleVehicleTypeSelect}
                                      options={vehicleTypeSelectOptions}
                                      width="100%"
                                      maxWidth="100%"
                                    />
                                </div>
                            </div>
                            {isAddingType && (
                                <>
                                    <div className="vc-fr fr">
                                        {renderTooltipLabel('Vehicle class', '__vehicleClass__', null)}
                                        <div className="vc-dd-wrap">
                                            <DropDownList
                                              placeholder="Select vehicle class"
                                              selectedValue={isAddingClass
                                                  ? { value: ADD_VEHICLE_CLASS_VALUE, label: '+ Add vehicle class' }
                                                  : selectedAddClass}
                                              setSelectedValue={handleAddClassSelect}
                                              options={vehicleClassSelectOptions}
                                              width="100%"
                                              maxWidth="100%"
                                            />
                                        </div>
                                    </div>
                                    {isAddingClass && (
                                        <div className="vc-fr fr">
                                            {renderTooltipLabel('New vehicle class', '__newVehicleClass__', null)}
                                            <input
                                              autoComplete="off"
                                              type="text"
                                              className="vc-fr-in fr-in"
                                              placeholder="e.g. Marine"
                                              value={addClassDraft}
                                              disabled={addTypePending}
                                              onChange={(e) => setAddClassDraft(e.target.value)}
                                            />
                                        </div>
                                    )}
                                    <div className="vc-fr fr">
                                        {renderTooltipLabel('New body type', '__newVehicleType__', null)}
                                        <div className="vc-add-type-row">
                                            <input
                                              autoComplete="off"
                                              type="text"
                                              className="vc-fr-in fr-in"
                                              placeholder="e.g. Pickup / 4x4"
                                              value={addTypeDraft}
                                              disabled={addTypePending}
                                              onChange={(e) => setAddTypeDraft(e.target.value)}
                                              onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      commitAddType();
                                                  } else if (e.key === 'Escape') {
                                                      e.preventDefault();
                                                      closeAddType({ restorePreviousType: true });
                                                  }
                                              }}
                                            />
                                            <PrimaryButton
                                              label={addTypePending ? 'Adding…' : 'Add'}
                                              width="auto"
                                              buttonHeight="2.375rem"
                                              fontSize="0.875rem"
                                              lineHeight="1.25rem"
                                              disable={addTypePending}
                                              onClickFunction={commitAddType}
                                            />
                                            <SecondaryButton
                                              label="Cancel"
                                              width="auto"
                                              buttonHeight="2.375rem"
                                              fontSize="0.875rem"
                                              lineHeight="1.25rem"
                                              imageIcon={false}
                                              onClickFunction={() => closeAddType({ restorePreviousType: true })}
                                            />
                                        </div>
                                        {addTypeError && (
                                            <div className="vc-fr-cap fr-cap vehicle-config-inline-error">{addTypeError}</div>
                                        )}
                                    </div>
                                </>
                            )}
                            <div className="vc-fr fr vc-fr-wide fr-wide">
                                {renderTooltipLabel('Description', '__description__', null)}
                                <div className="vc-textarea-inline">
                                    <TextArea descriptionValue={descriptionValue} setDescriptionValue={setDescriptionValue} width="100%" />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="fuelTariff" />
                                Fuel Type &amp; Tariff
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {renderFieldRow(FUEL_TYPE_FIELD_KEY)}
                            <div className="vc-fr fr">
                                {renderTooltipLabel('Active Price', '__activePrice__', null)}
                                <input
                                  className="vc-fr-in fr-in ro vc-fr-in-ro"
                                  readOnly
                                  value={activePriceDisplay || 'Select fuel type or tariff'}
                                  aria-readonly
                                />
                            </div>
                            <div className="vc-fr-cap fr-cap">
                                <span>Current price from Fuel &amp; Energy Price Registry</span>
                            </div>
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="dataSource" />
                                Crash & Braking Verification
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {renderFieldRow(BRAKE_DATA_SOURCE_FIELD_KEY)}
                        </div>
                    </section>

                    <HardwarePolicyBindingsPanel
                      fuelPolicyOptions={fuelPolicyOptions}
                      selectedFuelPolicy={selectedFuelPolicy}
                      onFuelPolicyChange={onFuelPolicyChange}
                      batteryPolicyOptions={batteryPolicyOptions}
                      selectedBatteryPolicy={selectedBatteryPolicy}
                      onBatteryPolicyChange={onBatteryPolicyChange}
                    />

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="fuelPolicy" />
                                Fuel Policy
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            <div className="vc-pol-ref">
                                <div
                                  className="has-tooltip"
                                  data-tt-title="Tank profile"
                                  data-tt-desc="Summary of the fuel tank geometry and detection parameters applied by this preset."
                                >
                                    {/* <div className="vc-pol-name">{vehicleType.trim() || 'Preset tank profile'}</div> */}
                                    <div className="vc-pol-name">Fuel Tank Setup                                    </div>
                                    <div className="vc-pol-meta">Configure capacity, detection, and stabilization</div>
                                </div>
                            </div>
                            <div className="vc-grp-lbl grp-lbl">Parameters</div>
                            {fuelPolicyLinked ? (
                                <div className="vc-fr-cap fr-cap">
                                    <span>
                                        Tank setup values are prefilled from the linked Fuel Policy. You can adjust them here.
                                    </span>
                                </div>
                            ) : null}
                            {fuelPolicyKeys.map((k) => renderFieldRow(k))}
                            {/* Fuel calibration map (locked) — hidden until wired up
                            <div className="vc-fr fr">
                                {renderTooltipLabel('Fuel calibration map', '__fuelCalibrationMap__', 'lock')}
                                <input className="vc-fr-in fr-in ro vc-fr-in-ro" readOnly value="Map · 8 rows" aria-readonly />
                            </div>
                            */}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="battery" />
                                Battery Diagnostic
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            <div className="vc-pol-ref">
                                <div
                                  className="has-tooltip"
                                  data-tt-title="Battery diagnostic profile"
                                  data-tt-desc="Voltage and timing thresholds used for starter battery health, alternator checks, and parasitic drain."
                                >
                                    {/* <div className="vc-pol-name">{vehicleType.trim() || 'Battery diagnostic profile'}</div> */}
                                    <div className="vc-pol-name">Battery diagnostic profile</div>
                                    <div className="vc-pol-meta">Crank, alternator, and parasitic draw</div>
                                </div>
                            </div>
                            <div className="vc-grp-lbl grp-lbl">Alert tuning</div>
                            {fieldsInGroup(GROUP.BATTERY).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="motion" />
                                Motion &amp; Idling Logic
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {fieldsInGroup(GROUP.MOTION).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="immobilizer" />
                                Security & Remote Immobilization
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {fieldsInGroup(GROUP.SECURITY).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="accelerometer" />
                                Accelerometer &amp; Vibration Calibration
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            <div className="vc-fr fr vc-fr-wide fr-wide">
                                {renderTooltipLabel('Vibration X / Y / Z (G)', '__vibrationGroup__', null)}
                                <div className="vc-tri tri">
                                    {accelAxisKeys.map((k) => (
                                        <input
                                          key={k}
                                          name={k}
                                          autoComplete="off"
                                          type="number"
                                          className="vc-fr-in fr-in"
                                          value={fieldValues[k] ?? ''}
                                          onChange={(e) => onFieldChange(k, e.target.value)}
                                        />
                                    ))}
                                </div>
                            </div>
                            {renderFieldRow('High Impact / Crash Sensitivity (G)')}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="energy" />
                                Energy Efficiency
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {renderFieldRow('Optimal Max Refill Limit (%)')}
                            {renderFieldRow('Optimal Max EV Charge Limit (%)')}
                            {/* Receipt Calib. — hidden until in use
                            {renderFieldRow('Receipt Calibration Multiplier')}
                            */}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="trip" />
                                Trip Boundaries
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {fieldsInGroup(GROUP.TRIP).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="hv" />
                                EV High-Voltage Health
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {fieldsInGroup(GROUP.EV).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="ice" />
                                ICE Health &amp; Mechanical Diagnostics
                            </div>
                        </div>
                        <div className="vc-panel-body">
                            {fieldsInGroup(GROUP.ICE).map((k) => renderFieldRow(k))}
                        </div>
                    </section>

                    <section className="vc-panel vc-panel-span-2">
                        <div className="vc-panel-hdr">
                            <div className="vc-panel-title">
                                <PanelTitleIcon name="gnss" />
                                GNSS Virtual Fuel (Fallback)
                            </div>
                        </div>
                        <div className="vc-panel-body vc-panel-body-gnss">
                            {fuelPolicyLinked ? (
                                <div className="vc-fr-cap fr-cap">
                                    <span>
                                        Six coefficients are filled from the linked Fuel Policy.
                                        Speed zones and toggles are set on this preset.
                                    </span>
                                </div>
                            ) : null}
                            {gnssPanelKeys.map((k) => renderFieldRow(k))}
                        </div>
                    </section>
            </div>
            {tooltipLayer}
            {isManagingTypes && (
                <div className="modal-container-table-bg">
                    <VehicleTypeManagerModal onClose={() => setIsManagingTypes(false)} />
                </div>
            )}
        </div>
    );
};
